/* eslint-disable react/no-array-index-key */
import {
    Box, Button, Heading, HStack, SimpleGrid, Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { OccupantContact } from '@acp/shared';
import { FaIcon, SkeletonMotion, ThemeTokens } from '@acp/core-ui';
import copy from 'copy-to-clipboard';
import { faEnvelope, faPhone } from '@fortawesome/pro-light-svg-icons';
import { useTenancy } from '../../../../tenancy';
import { AccessIssuesWizardMachineContext } from '../machine/context';

function Skeleton() {
    // The text in these is supposed to be like this!
    return (
        <Box color="white">
            <SkeletonMotion>
                <Text fontWeight="medium" mb={2}>
                    a
                </Text>
            </SkeletonMotion>
            <VStack alignItems="stretch" gap={1}>
                <SkeletonMotion h={5}>
                    a
                </SkeletonMotion>
                <SkeletonMotion h={5}>
                    a
                </SkeletonMotion>
                <SkeletonMotion h={5}>
                    a
                </SkeletonMotion>
            </VStack>
        </Box>
    );
}

function OccupantRenderer({ occupant }: {
    occupant: OccupantContact;
}) {
    const toast = useToast();

    const copyToClipboard = (text: string) => {
        copy(text);
        toast({
            title: `Copied ${text} to clipboard`,
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    return (
        <Box color="black">
            <Text fontWeight="medium" mb={2}>{ occupant.name }</Text>
            <VStack alignItems="stretch" gap={1}>
                {
                    occupant.email !== null
                    && (
                        <HStack alignItems="center">
                            <FaIcon icon={faEnvelope} color="black" />
                            <Button
                                variant="link"
                                fontSize="sm"
                                onClick={() => copyToClipboard(occupant.email!)}
                                color="black"
                                colorScheme="blackAlpha"
                                fontWeight="normal"
                                userSelect="text"
                            >
                                { occupant.email }
                            </Button>
                        </HStack>
                    )
                }
                { occupant.contactNumbers.map(({ number, type }, idx) => (
                    <HStack key={idx} alignItems="center">
                        <FaIcon icon={faPhone} color="black" />
                        <Button
                            variant="link"
                            fontSize="sm"
                            onClick={() => copyToClipboard(number!)}
                            color="black"
                            colorScheme="blackAlpha"
                            fontWeight="normal"
                            userSelect="text"
                        >
                            { number }
                        </Button>
                        <Text fontSize="sm" color="black">
                            (
                            { type?.toLowerCase() }
                            )
                        </Text>
                    </HStack>
                ))}
            </VStack>
        </Box>
    );
}

function OccupantGrid({
    occupants,
}: {
    occupants: OccupantContact[];
}) {
    if (occupants.length === 0) {
        return <Text>This property is currently vacant</Text>;
    }

    return (
        <SimpleGrid columns={[1, 2, 3, 3]} columnGap={3} rowGap={3}>
            { occupants.map((occupant, idx) => (
                <OccupantRenderer key={idx} occupant={occupant} />
            ))}
        </SimpleGrid>
    );
}

function AccessIssuesWizardTenantDetails() {
    const propertyId = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.propertyId
    );

    const tenancy = useTenancy(propertyId);

    return (
        <VStack bgColor={ThemeTokens.Defaults.bgColor} color="black" w="100%" py={4} px={5} alignItems="stretch" gap={3}>
            <Heading fontSize="lg" color="black">
                Tenancy contact details
            </Heading>
            { tenancy.data === undefined && (
                <SimpleGrid columns={3} columnGap={3} rowGap={3}>
                    <Skeleton />
                    <Skeleton />
                </SimpleGrid>
            )}
            { tenancy.data !== undefined
                && tenancy.data.active === null
                && <Text>This property is currently vacant</Text>}
            { tenancy.data !== undefined
                && tenancy.data.active !== null
                && <OccupantGrid occupants={tenancy.data.active.occupants} />}
        </VStack>
    );
}

export { AccessIssuesWizardTenantDetails };
